import appConfig from '../../config/appConfig';
import * as httpErrorsTypes from '../../consts/app/httpErrorsTypes';
import * as iccProductsStatusMessageTypes from '../../consts/icc/iccProductsStatusMessageTypes';
import * as iccResponseErrorTypes from '../../consts/icc/iccResponseErrorTypes';
import * as loaderNames from '../../consts/loader/loaderNames';
import * as logMessageTypes from '../../consts/log/logMessageTypes';
import * as muleSoftResponseErrorTypes from '../../consts/muleSoft/muleSoftResponseErrorTypes';
import ICC_PRODUCT_STATUS_TYPES from '../../enums/icc/iccProductStatusTypes';
import {
    removeConsumerProductsStatus,
    setConsumerProductsStatus,
    updateConsumerProductData,
    updateConsumerProductsStatus,
} from '../../state/ducks/consumer';
import {hideLoader, setHolderRegisterFailed, setIsDoubleRegistration} from '../../state/ducks/global';
import {setDeviceActivationStatus} from '../../state/ducks/iotDevice/actions';
import {showManualRegistrationModalAction, showPurchaseDateModalAction} from '../../state/ducks/modal';
import {
    removeProductToChangeRegistrationStatus,
    setDevicePurchaseData,
    setHolderPurchaseData,
    setProductToChangeRegisteringStatus,
    setProductToChangeUnregisteringStatus,
} from '../../state/ducks/productSettings';
import {makeSelectIotDeviceMergedWithConsumerProduct, makeSelectProductStatus} from '../../state/selectors/consumer';
import {selectIsNetworkOnline} from '../../state/selectors/global';
import {selectIotDeviceType, selectIsDeviceInSblMode} from '../../state/selectors/iotDevice';
import {
    makeSelectCurrentProductToChangeRegistrationStatus,
    makeSelectDevicePurchaseDate,
    makeSelectHolderPurchaseDate,
} from '../../state/selectors/productSettings';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import {amplitudeRegistrationTracking} from '../amplitude';
import analyticsService from '../analyticsService';
import appErrorService from '../app/appErrorService';
import backendService from '../app/backendService';
import cryptoService from '../cryptoService';
import ConsumerAssetsError from '../errors/consumerAssetsError';
import iccMarketService from '../icc/iccMarketService';
import log from '../logger/log';
import marketTypeService from '../marketTypeService';
import muleSoftMarketService from '../muleSoft/muleSoftMarketService';
import muleSoftProductService from '../muleSoft/muleSoftProductService';
import yapService from '../yap/yapService';
import yapStateService from '../yap/yapStateService';

let productRegistrationAttempts = 0;

const setProductRegistrationAttempts = (attempts) => {
    productRegistrationAttempts = attempts;
};

const getProductRegistrationAttempts = () => productRegistrationAttempts;

const muleSoftUnregistration = async ({deviceSerialNumber, codentify, isHolder}) => {
    const setMulesoftProductStatus = (status) => {
        const data = {
            deviceSerialNumber,
            codentify,
            status,
        };

        if (isHolder) {
            dispatch(updateConsumerProductsStatus(data));
        } else {
            dispatch(setConsumerProductsStatus([data]));
        }
    };

    setMulesoftProductStatus(ICC_PRODUCT_STATUS_TYPES.UNREGISTERING);

    dispatch(
        setProductToChangeUnregisteringStatus({
            deviceSerialNumber,
            codentify,
            isHolder,
            isSchedulerRequired: false,
        })
    );

    const {isUnregistered} = await muleSoftProductService.unlinkProduct(codentify, isHolder);

    if (isUnregistered) {
        setMulesoftProductStatus(ICC_PRODUCT_STATUS_TYPES.UNREGISTERED);
    } else {
        setMulesoftProductStatus(ICC_PRODUCT_STATUS_TYPES.FAILED);
    }
};

const iccUnregistration = async ({productIdentify, deviceSerialNumber, codentify, isHolder}) => {
    const response = await iccMarketService.removeConsumerProduct({data: productIdentify});

    if (response) {
        dispatch(
            updateConsumerProductData({
                deviceSerialNumber,
                codentify,
                status: ICC_PRODUCT_STATUS_TYPES.UNREGISTERING,
            })
        );
        dispatch(setProductToChangeUnregisteringStatus({deviceSerialNumber, codentify, isHolder}));
    }
};

const unregisterConsumerProduct = async ({productId, codentify, deviceSerialNumber, isHolder}) => {
    const isDCE2 = marketTypeService.isDCE2();

    try {
        const msg = isHolder ? 'Holder ' : '';

        log.info(`Start ${msg}unregistration process for codentify: ${codentify}.`);

        dispatch(removeConsumerProductsStatus());

        const productIdentify = productId ? {productId} : {codentifyId: codentify};

        if (isDCE2) {
            await muleSoftUnregistration({deviceSerialNumber, codentify, isHolder});
        } else {
            await iccUnregistration({productIdentify, deviceSerialNumber, codentify, isHolder});
        }
    } catch (e) {
        const isConsumerAssetsError = e instanceof ConsumerAssetsError;
        const type = selectIotDeviceType(getState());

        appErrorService.showGlobalError();
        analyticsService.pushUnregistrationFlowErrorEvent(type, e?.code);

        if (isDCE2 && !isConsumerAssetsError) {
            const isNetworkOnline = selectIsNetworkOnline(getState());

            if (isNetworkOnline) {
                await muleSoftMarketService.getConsumerAssets();
            } else {
                await muleSoftProductService.getConsumerAssetsInterval();
            }
        }

        throw e;
    }
};

const purchaseRegisterDeviceCheck = ({action, isDoubleRegistration, cancelAction, isPurchaseDate}) => {
    const isPurchaseDateEnabled = appConfig.getIsPurchaseDateEnabled();
    const isDeviceInSblMode = selectIsDeviceInSblMode(getState());

    if (isDeviceInSblMode) return;

    if (isPurchaseDateEnabled && !isDoubleRegistration && !isPurchaseDate) {
        dispatch(showPurchaseDateModalAction({registrationAction: action, registrationCancelAction: cancelAction}));
    } else {
        action();
    }
};

const LINK_CONSUMER_PRODUCT_ATTEMPT_LIMIT = 3;
let currentLinkConsumerProductChargerAttempt = 0;
let currentLinkConsumerProductHolderAttempt = 0;

const setLinkConsumerProductAttempt = (isHolder, isClearAttempts) => {
    const isManualRegistrationEnabled = appConfig.getIsManualRegistrationEnabled();

    if (!isManualRegistrationEnabled) return;

    if (isHolder) {
        currentLinkConsumerProductHolderAttempt++;
    } else {
        currentLinkConsumerProductChargerAttempt++;
    }

    if (isClearAttempts) {
        if (isHolder) {
            currentLinkConsumerProductHolderAttempt = 0;
        } else {
            currentLinkConsumerProductChargerAttempt = 0;
        }
    }
};

const clearConsumerLinkAttempt = () => {
    currentLinkConsumerProductHolderAttempt = 0;
    currentLinkConsumerProductChargerAttempt = 0;
};

const muleSoftRegistration = async ({codentify, isHolder, setMuleSoftProductStatus}) => {
    if (!codentify) {
        const isManualRegistrationEnabled = appConfig.getIsManualRegistrationEnabled();

        if (isManualRegistrationEnabled) {
            dispatch(showManualRegistrationModalAction({isHolder}));
            return;
        }

        setMuleSoftProductStatus(ICC_PRODUCT_STATUS_TYPES.FAILED, muleSoftResponseErrorTypes.ERROR);

        return;
    }

    const deviceAttemptCount = isHolder
        ? currentLinkConsumerProductHolderAttempt
        : currentLinkConsumerProductChargerAttempt;
    const isRegistrationAttemptEnable = LINK_CONSUMER_PRODUCT_ATTEMPT_LIMIT > deviceAttemptCount;

    const registerMuleSoftConsumerProduct = async () => {
        try {
            setLinkConsumerProductAttempt(isHolder);

            setMuleSoftProductStatus(ICC_PRODUCT_STATUS_TYPES.REGISTERING);

            amplitudeRegistrationTracking.trackRegistrationStarted(isHolder);
            const {isRegistered} = await muleSoftProductService.linkProduct(codentify, isHolder);

            if (!isRegistered && isRegistrationAttemptEnable) {
                setMuleSoftProductStatus(ICC_PRODUCT_STATUS_TYPES.FAILED, muleSoftResponseErrorTypes.NOT_FOUND);

                return false;
            }
            setLinkConsumerProductAttempt(isHolder, true);

            setMuleSoftProductStatus(ICC_PRODUCT_STATUS_TYPES.REGISTERED);

            return true;
        } catch (e) {
            muleSoftProductService.updateMuleSoftDeviceStatus(isHolder, ICC_PRODUCT_STATUS_TYPES.FAILED);

            const errorStatus = e?.response?.status;
            const mapErrorCode = (errorStatus) => {
                switch (errorStatus) {
                    case httpErrorsTypes.NOT_FOUND:
                        return muleSoftResponseErrorTypes.NOT_FOUND;
                    case httpErrorsTypes.CONFLICT:
                        return muleSoftResponseErrorTypes.ALREADY_OWNED;
                    default:
                        return muleSoftResponseErrorTypes.ERROR;
                }
            };
            const error = mapErrorCode(errorStatus);

            amplitudeRegistrationTracking.trackRegistrationGamError(isHolder);

            if (isRegistrationAttemptEnable) {
                log.error(`Registration process is failed. isHolder: ${isHolder} Error: ${e}`, {
                    logMessageType: logMessageTypes.GAM,
                });
                setMuleSoftProductStatus(ICC_PRODUCT_STATUS_TYPES.FAILED, error);
            } else {
                dispatch(removeProductToChangeRegistrationStatus({codentify}));
            }

            if (isHolder) {
                dispatch(setHolderRegisterFailed(true));
            }

            return false;
        }
    };

    const isRegistered = await registerMuleSoftConsumerProduct();

    if (!isRegistered && !isRegistrationAttemptEnable) {
        dispatch(hideLoader(loaderNames.REGISTRATION_FAILED_POPUP_REGISTER_LOADER));

        setLinkConsumerProductAttempt(isHolder, true);

        dispatch(showManualRegistrationModalAction({isHolder, isAutoRegistrationFailed: true}));
    }
};

const fetchIccConsumerProduct = async ({codentify, deviceSerialNumber, purchasedOn}) => {
    if (codentify) {
        log.info(`Start registration process for codentify: ${codentify}`);

        return iccMarketService.setConsumerProductV2({codentify, purchasedOn});
    }

    const encryptedSerialNumber = cryptoService.encryptDeviceSerialNumber(deviceSerialNumber);

    if (encryptedSerialNumber) {
        log.info(`Start registration process for serialNumber: ${encryptedSerialNumber}`);

        return iccMarketService.setConsumerProductV3({deviceSerialNumber: encryptedSerialNumber, purchasedOn});
    }

    log.error(`Registration process is failed. Codentify and serialNumber are empty`);

    return null;
};

const iccRegistration = async ({
    codentify,
    purchasedOn,
    deviceSerialNumber,
    setProductStatusError,
    dispatchSetProductToChangeRegisteringStatus,
    isHolder,
}) => {
    let response;

    try {
        amplitudeRegistrationTracking.trackRegistrationStarted(isHolder);

        response = await fetchIccConsumerProduct({codentify, deviceSerialNumber, purchasedOn});

        if (response) {
            if (codentify) {
                dispatch(updateConsumerProductData({codentify, status: ICC_PRODUCT_STATUS_TYPES.REGISTERING}));
            } else {
                dispatch(updateConsumerProductData({deviceSerialNumber, status: ICC_PRODUCT_STATUS_TYPES.REGISTERING}));
            }
            dispatchSetProductToChangeRegisteringStatus();
        } else {
            setProductStatusError(iccProductsStatusMessageTypes.INVALID_DEVICE_STATUS);
        }
    } catch (e) {
        const errorStatus = e?.response?.status;
        const isRegistrationFailedError = errorStatus
            ? iccResponseErrorTypes.isRegistrationFailedIccHttpError(errorStatus)
            : false;

        if (isRegistrationFailedError) {
            amplitudeRegistrationTracking.trackRegistrationIccError({isHolder, error: {errorCode: errorStatus}});
        } else {
            log.error(`Registration process is failed. Error: ${e}`, {logMessageType: logMessageTypes.ICC});
            setProductStatusError(iccProductsStatusMessageTypes.EXTERNAL_SYSTEM_UNAVAILABLE);
        }
    }

    return response;
};

const registerDevice = async (registerData) => {
    const {
        deviceSerialNumber: serialNumber,
        holderInternalId,
        codentify,
        name,
        isHolder,
        purchasedOn = undefined,
    } = registerData;

    const currentProductToChangeRegistrationStatus = makeSelectCurrentProductToChangeRegistrationStatus(!!isHolder)(
        getState()
    );

    if (currentProductToChangeRegistrationStatus) return;

    const dispatchSetProductToChangeRegisteringStatus = (isSchedulerRequired) => {
        dispatch(
            setProductToChangeRegisteringStatus({
                deviceSerialNumber,
                codentify,
                name,
                isHolder,
                isSchedulerRequired,
            })
        );
    };
    const setProductStatusError = (statusMessage) => {
        dispatch(
            updateConsumerProductsStatus({
                deviceSerialNumber,
                codentify,
                status: ICC_PRODUCT_STATUS_TYPES.FAILED,
                statusMessage,
            })
        );
        dispatchSetProductToChangeRegisteringStatus(false);
    };

    const setMuleSoftProductStatus = (status, statusMessage) => {
        dispatchSetProductToChangeRegisteringStatus(false);

        const data = {
            deviceSerialNumber,
            codentify,
            status,
            statusMessage,
        };

        dispatch(updateConsumerProductsStatus(data));
    };

    const isDCE2 = marketTypeService.isDCE2();

    if (!isDCE2) {
        dispatch(removeConsumerProductsStatus());
    }

    dispatch(isHolder ? setHolderPurchaseData(purchasedOn) : setDevicePurchaseData(purchasedOn));

    const deviceSerialNumber = serialNumber || holderInternalId;

    if (isDCE2) {
        return await muleSoftRegistration({codentify, isHolder, setMuleSoftProductStatus});
    } else {
        return await iccRegistration({
            codentify,
            purchasedOn,
            deviceSerialNumber,
            setProductStatusError,
            dispatchSetProductToChangeRegisteringStatus,
            isHolder,
        });
    }
};

const registerConsumerProduct = async (product, name, registrationCancelAction) => {
    if (!product) return;

    const {device, holder} = product;
    const {codentifyOriginal: codentify, deviceSerialNumber} = device;
    const holderInternalId = holder?.deviceSerialNumber;
    const isDeviceRegisterationNeeded = !isStatusRegistered(device.status);
    const isHolderRegistrationNeeded = holderInternalId && !isStatusRegistered(holder?.status);
    const isDoubleRegistration = isDeviceRegisterationNeeded && isHolderRegistrationNeeded;

    if (isDoubleRegistration) {
        dispatch(setIsDoubleRegistration(true));
    }
    const state = getState();
    const isDevicePurchaseDateInState = makeSelectDevicePurchaseDate()(state);
    const isHolderPurchaseDateInState = makeSelectHolderPurchaseDate()(state);

    purchaseRegisterDeviceCheck({
        action: (kitPurchasedOn) => {
            if (isDeviceRegisterationNeeded) {
                purchaseRegisterDeviceCheck({
                    action: (purchasedOn) => {
                        const registerData = {
                            deviceSerialNumber,
                            codentify,
                            name,
                            isHolder: false,
                            purchasedOn: isDevicePurchaseDateInState || kitPurchasedOn || purchasedOn,
                        };

                        registerDevice(registerData);
                    },
                    isPurchaseDate: isDevicePurchaseDateInState,
                    isDoubleRegistration,
                    cancelAction: () => {
                        helpers.runFunction(registrationCancelAction);
                    },
                });
            }
            if (isHolderRegistrationNeeded) {
                purchaseRegisterDeviceCheck({
                    action: (purchasedOn) => {
                        const registerData = {
                            holderInternalId,
                            codentify: holder.codentify,
                            name,
                            isHolder: true,
                            purchasedOn: isHolderPurchaseDateInState || kitPurchasedOn || purchasedOn,
                        };

                        registerDevice(registerData);
                    },
                    isPurchaseDate: isHolderPurchaseDateInState,
                    isDoubleRegistration,
                    cancelAction: () => {
                        helpers.runFunction(registrationCancelAction);
                    },
                });
            }
        },
        isDoubleRegistration: !isDoubleRegistration || (isDevicePurchaseDateInState && isHolderPurchaseDateInState),
        cancelAction: () => {
            helpers.runFunction(registrationCancelAction);
        },
    });
};

const fetchProductsStatusIfNeeded = () => {
    const isDCE2 = marketTypeService.isDCE2();

    if (isDCE2) return;

    const currentProductToChangeRegistrationStatus = makeSelectCurrentProductToChangeRegistrationStatus()(getState());

    if (currentProductToChangeRegistrationStatus) {
        const {isHolder} = currentProductToChangeRegistrationStatus;

        fetchProductsStatusAndCheckIotProductStatus({isHolder});
    }
};

const fetchProductsStatusAndCheckIotProductStatus = async ({isHolder, doNotCallGetConsumerProducts}) => {
    const iotProduct = makeSelectIotDeviceMergedWithConsumerProduct()(getState());

    if (!iotProduct) return;

    const device = isHolder ? iotProduct.holder : iotProduct.device;

    if (!device) return;

    const {status, codentify, deviceSerialNumber} = device;

    await iccMarketService.getConsumerProductsStatus();

    if (doNotCallGetConsumerProducts) return;

    const productStatus = makeSelectProductStatus(deviceSerialNumber, codentify)(getState());

    if (!productStatus || productStatus.status !== status) {
        await iccMarketService.getConsumerProducts();
    }
};

const checkProductsStatusBeforePairing = async () => {
    const isDCE2 = marketTypeService.isDCE2();

    try {
        if (isDCE2) {
            await muleSoftMarketService.getConsumerAssets();
        } else {
            await iccMarketService.getConsumerProducts();
        }
    } catch (e) {
        log.error(`productService: checkProductsStatusBeforePairing, error: ${e}`);
    }
};

const isStatusRegistered = (status) => status === ICC_PRODUCT_STATUS_TYPES.REGISTERED;

const isStatusRegistering = (status) => status === ICC_PRODUCT_STATUS_TYPES.REGISTERING;

const isStatusUnregistered = (status) => status === undefined || status === ICC_PRODUCT_STATUS_TYPES.UNREGISTERED;

const isStatusUnregistering = (status) => status === ICC_PRODUCT_STATUS_TYPES.UNREGISTERING;

const isStatusRegisterFailed = (status) => status === ICC_PRODUCT_STATUS_TYPES.FAILED;

const proceedDeviceStatus = (statusFunction, status) => statusFunction(status);

const getIsRegistered = (status) => proceedDeviceStatus(isStatusRegistered, status);

const getIsRegistering = (status) => proceedDeviceStatus(isStatusRegistering, status);

const getIsUnregistered = (status) => proceedDeviceStatus(isStatusUnregistered, status);

const getIsRegisterFailed = (status) => proceedDeviceStatus(isStatusRegisterFailed, status);

const getIsUnregistering = (status) => proceedDeviceStatus(isStatusUnregistering, status);

const checkDeviceActivatedAndRegisterIfNeeded = async () => {
    if (backendService.isGamBackend()) {
        //to setup activation status
        await yapStateService.checkIsDeviceActivated();
        return;
    }

    const iotProduct = makeSelectIotDeviceMergedWithConsumerProduct()(getState());
    const {device} = iotProduct || {};
    const {isP4} = device || {};
    const isYAPFlowEnabled = yapService.isYapMode();

    if (!isP4 || !isYAPFlowEnabled) {
        dispatch(setDeviceActivationStatus(true));
    }
};

const isSkipRegistrationEnabled = (isP4) =>
    appConfig.getIsNonMandatoryRegistrationEnabled() &&
    !yapService.isYapEncryptedModeAndP4(isP4) &&
    backendService.isUamBackend();

export default {
    checkDeviceActivatedAndRegisterIfNeeded,
    clearConsumerLinkAttempt,
    fetchProductsStatusAndCheckIotProductStatus,
    fetchProductsStatusIfNeeded,
    getIsRegistered,
    getIsRegistering,
    getIsUnregistered,
    getIsRegisterFailed,
    getIsUnregistering,
    getProductRegistrationAttempts,
    isStatusRegistered,
    purchaseRegisterDeviceCheck,
    registerConsumerProduct,
    registerDevice,
    setProductRegistrationAttempts,
    unregisterConsumerProduct,
    setLinkConsumerProductAttempt,
    checkProductsStatusBeforePairing,
    isSkipRegistrationEnabled,
};
